<template>
  <v-app style="background-color: #f7f8fc;">
    <v-container fluid class="pa-8">
      <!-- 1. Encabezado Mejorado -->
      <v-row align="center" class="mb-6">
        <v-col cols="auto">
          <v-btn icon text @click="goBack" aria-label="Volver">
            <v-icon>arrow_back</v-icon>
          </v-btn>
        </v-col>
        <v-col>
          <h1 class="text-h4 font-weight-bold grey--text text--darken-3">
            Historia Clínica
          </h1>
          <div class="text-subtitle-1 grey--text text--darken-1">
            {{ paciente.id }} - {{ paciente.nombre }} {{ paciente.apellido }}
          </div>
        </v-col>
        <v-col cols="auto">
          <v-btn
            color="primary"
            depressed
            @click="generarPDF"
            :disabled="!paciente.id || !!historyLoadError"
            class="rounded-lg"
          >
            <v-icon left>picture_as_pdf</v-icon>
            Descargar PDF
          </v-btn>
        </v-col>
      </v-row>

      <!-- 2. Tarjetas de Información -->
      <v-row class="mb-8">
        <v-col cols="12" md="6">
          <v-card outlined class="pa-4 rounded-lg fill-height">
            <v-card-title class="text-h6 font-weight-medium grey--text text--darken-3 d-flex align-center">
              <span>Información Personal</span>
              <v-spacer></v-spacer>
              <v-btn v-if="!esPaciente && !pacienteEditando" small text color="primary" @click="activarEdicionPaciente">Editar</v-btn>
              <v-btn v-else-if="!esPaciente && pacienteEditando" small color="primary" @click="guardarPacienteDesdeHistoria">Guardar</v-btn>
            </v-card-title>
            <v-card-text class="text-body-1">
              <div v-if="!pacienteEditando">
                <p><strong class="font-weight-medium">Nombre:</strong> {{ paciente.nombre }} {{ paciente.apellido }}</p>
                <p><strong class="font-weight-medium">Edad:</strong> {{ paciente.edad }} años</p>
                <p><strong class="font-weight-medium">Género:</strong> {{ paciente.genero }}</p>
              </div>
              <v-form v-else @submit.prevent="guardarPacienteDesdeHistoria">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="pacienteForm.name" label="Nombre" dense outlined></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="pacienteForm.lastname" label="Apellido" dense outlined></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="pacienteForm.dni" label="DNI" dense outlined></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="pacienteForm.dob" label="Fecha de nacimiento" type="date" dense outlined></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select v-model="pacienteForm.gender" :items="opcionesGenero" item-text="text" item-value="value" label="Género" dense outlined></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="pacienteForm.telefono" label="Teléfono" dense outlined></v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card outlined class="pa-4 rounded-lg fill-height">
            <v-card-title class="text-h6 font-weight-medium grey--text text--darken-3 d-flex align-center">
              <span>Obra Social</span>
              <v-spacer></v-spacer>
              <v-btn v-if="!esPaciente && pacienteEditando" small text color="primary" @click="cancelarEdicionPaciente">Cancelar</v-btn>
            </v-card-title>
            <v-card-text class="text-body-1">
              <div v-if="!pacienteEditando">
                <p><strong class="font-weight-medium">Aseguradora:</strong> {{ seguro.name || 'No especificada' }}</p>
                <p><strong class="font-weight-medium">Nº Afiliado:</strong> {{ seguro.number || 'No especificado' }}</p>
              </div>
              <v-row v-else dense>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="pacienteForm.seguroId"
                    :items="seguroOptions"
                    item-text="name"
                    item-value="value"
                    label="Aseguradora"
                    dense
                    outlined
                    clearable
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="pacienteForm.seguroNumber" label="Nº Afiliado" dense outlined></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-alert v-if="historyLoadError" type="warning" text class="mb-6">
        {{ historyLoadError }}
      </v-alert>

      <!-- 3. Pestañas -->
      <v-tabs v-model="tab" color="primary" grow class="mb-6">
        <v-tab>Historial de Consultas</v-tab>
        <v-tab v-if="mostrarTabFormulario">{{ formularioConsultaTitulo }}</v-tab>
      </v-tabs>

      <v-window v-model="tab">
        <!-- Pestaña 1: Historial de Consultas -->
        <v-window-item :value="0">
          <!-- PDF de estudios del paciente (el médico los sube y el paciente los descarga en "Mis estudios") -->
          <EstudiosPdf v-if="paciente.id && !esPaciente" :paciente-id="paciente.id" class="mb-6" />
          <v-expansion-panels accordion>
            <v-expansion-panel v-for="consulta in consultas" :key="consulta.id" class="mb-3 rounded-lg">
              <v-expansion-panel-header>
                <v-row align="center" no-gutters>
                  <v-col cols="12" sm="4" class="font-weight-bold">
                    <v-icon left>event</v-icon>
                    {{ consulta.fecha }}
                  </v-col>
                  <v-col cols="12" sm="5">
                    <v-icon left>local_hospital</v-icon>
                    {{ consulta.medico }}
                  </v-col>
                  <v-col cols="12" sm="3" class="text-truncate">
                    <strong>Motivo:</strong> {{ consulta.motivo }}
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="d-flex justify-end mb-3" v-if="consulta.canEdit && !esPaciente">
                  <v-btn v-if="!esPaciente" color="primary" small outlined @click="editarConsulta(consulta)">
                    <v-icon left small>edit</v-icon>
                    Editar
                  </v-btn>
                </div>
                <div v-if="consulta.canEdit && consulta.editableUntil && !esPaciente" class="text-caption grey--text text--darken-1 mb-3 text-right">
                  Editable hasta {{ formatearFechaEdicion(consulta.editableUntil) }}
                </div>
                <v-card flat color="grey lighten-4" class="pa-4 mb-4 rounded-lg">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">Notas de la Consulta</h4>
                  <p>{{ consulta.tratamiento }}</p>
                </v-card>

                <v-row>
                  <v-col cols="12" md="6">
                     <h4 class="text-subtitle-1 font-weight-bold mb-3">Estudios Solicitados</h4>
                      <v-list v-if="consulta.estudios && consulta.estudios.length" two-line dense>
                        <v-list-item v-for="estudio in consulta.estudios" :key="estudio.id" class="mb-2">
                           <v-list-item-title class="font-weight-medium">{{ estudio.estudio_name }}</v-list-item-title>
                           <v-list-item-subtitle>{{ formatFecha(estudio.fecha_estudio) }} - {{ estudio.observaciones }}</v-list-item-subtitle>
                           <div v-if="estudio.estudio_indicador && estudio.estudio_indicador.length" class="mt-2">
                              <v-chip v-for="ind in estudio.estudio_indicador" :key="ind.id" small class="mr-2 mb-1">
                                {{ ind.indicador_name.nombre_indicador }}: {{ ind.valor_referencia }}
                              </v-chip>
                           </div>
                        </v-list-item>
                      </v-list>
                      <p v-else class="grey--text">Sin estudios registrados.</p>
                  </v-col>
                   <v-col cols="12" md="6">
                      <h4 class="text-subtitle-1 font-weight-bold mb-3">Medicación Recetada</h4>
                      <v-list v-if="consulta.medicacion && consulta.medicacion.length" dense>
                          <v-list-item v-for="med in consulta.medicacion" :key="med.id">
                            <v-list-item-title><strong>{{ med.medicacion.droga }}</strong> {{ med.dosis }}</v-list-item-title>
                            <v-list-item-subtitle v-if="med.frecuencia">Frecuencia: {{ med.frecuencia }}</v-list-item-subtitle>
                          </v-list-item>
                      </v-list>
                       <p v-else class="grey--text">Sin medicación registrada.</p>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
           <v-alert v-if="!consultas.length" type="info" text class="mt-4">
            No hay consultas registradas para este paciente.
          </v-alert>
        </v-window-item>

        <!-- Pestaña 2: Agregar Nueva Consulta -->
        <v-window-item v-if="mostrarTabFormulario" :value="1">
           <v-row>
            <v-col cols="12" md="7">
              <v-card outlined class="rounded-lg">
                <v-card-title class="text-h6 font-weight-medium">{{ formularioConsultaTitulo }}</v-card-title>
                <v-card-text>
                  <v-form @submit.prevent="crearHistoriaClinica">
                    <v-row>
                      <v-col cols="6">
                        <v-text-field v-model="nuevaConsulta.fecha_consulta" label="Fecha" readonly outlined dense></v-text-field>
                      </v-col>
                       <v-col cols="6">
                        <v-text-field v-model="nuevaConsulta.motivo" label="Motivo de la consulta" outlined dense></v-text-field>
                      </v-col>
                    </v-row>
                    <v-textarea v-model="nuevaConsulta.tratamiento" label="Notas de la consulta, diagnóstico y tratamiento" outlined rows="6"></v-textarea>

                    <div class="d-flex justify-space-between align-center mb-3">
                        <h4 class="text-subtitle-1 font-weight-bold">Medicación Recetada</h4>
                        <v-btn color="primary" text small @click="agregarMedicacion">Agregar Medicación</v-btn>
                    </div>
                    <v-data-table :headers="headers" :items="medicaciones" dense class="mb-4">
                        <template v-slot:item.actions1="{ item }"><v-icon small @click="updateItem(item)">edit</v-icon></template>
                        <template v-slot:item.actions2="{ item }"><v-icon small @click="deleteItem(item)">delete</v-icon></template>
                    </v-data-table>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="5">
              <v-card outlined class="rounded-lg">
                <v-card-title class="text-h6 font-weight-medium">Estudios y Laboratorio</v-card-title>
                 <v-card-text>
                    <div class="d-flex justify-space-between align-center mb-3">
                        <h4 class="text-subtitle-1 font-weight-bold">Solicitar Nuevo Estudio</h4>
                        <v-btn color="primary" text small @click="addItem(datos)">Agregar Estudio</v-btn>
                    </div>
                    <v-text-field v-model="estudios.estudio_name" label="Nombre del estudio" outlined dense></v-text-field>
                    <DatePicker v-model="estudios.fecha_estudio" label="Fecha" outlined dense></DatePicker>
                    <v-text-field v-model="estudios.observaciones" label="Observaciones" outlined dense></v-text-field>

                    <CrudComponent :title="cabecera" :datosCrud.sync="indicadores" :items.sync="datosTabla" @update:items="handleItems" />

                    <h4 class="text-subtitle-1 font-weight-bold mt-6 mb-2">Estudios Agregados</h4>
                    <v-data-table :headers="headersLab" :items="historiaEstudios" dense>
                        <template v-slot:item.actions1="{ item }"><v-icon small @click="updateItemIndicadores(item)">edit</v-icon></template>
                        <template v-slot:item.actions2="{ item }"><v-icon small @click="deleteItemIndicadores(item)">delete</v-icon></template>
                    </v-data-table>
                 </v-card-text>
              </v-card>
            </v-col>
          </v-row>
           <v-row v-if="paciente.id && !esPaciente">
            <v-col cols="12">
              <EstudiosPdf :paciente-id="paciente.id" />
            </v-col>
          </v-row>
          <v-row>
              <v-col>
                <v-btn type="submit" color="success" x-large block @click="crearHistoriaClinica" class="mt-4 rounded-lg">{{ guardarConsultaLabel }}</v-btn>
              </v-col>
              <v-col v-if="estaEditandoConsulta">
                <v-btn color="grey" x-large block outlined @click="cancelarEdicionConsulta" class="mt-4 rounded-lg">Cancelar edición</v-btn>
              </v-col>
          </v-row>
        </v-window-item>
      </v-window>

      <!-- Diálogo para medicación -->
      <v-dialog v-model="dialog" persistent max-width="740">
        <v-card class="rounded-lg">
          <v-card-title class="text-h5 font-weight-medium">Agregar Medicación</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="nuevaMedicacion.medicacion"
                  :items="medicamentosLista"
                  item-text="droga"
                  item-value="id"
                  label="Medicamento"
                  outlined
                  dense
                  return-object
                  @change="onMedicamentoSelect"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="nuevaMedicacion.presentacion"
                  label="Presentación"
                  outlined
                  dense
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="nuevaMedicacion.dosis"
                  label="Dosis"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="nuevaMedicacion.frecuencia"
                  label="Frecuencia"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-btn color="primary" :disabled="!nuevaMedicacion.medicacion" @click="confirmarMedicacion">
              <v-icon left>add</v-icon> Agregar
            </v-btn>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="closeDialog()">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script>
import CrudComponent from "@/components/CRUDComponent.vue"
import {get_indicadores, get_medicamentos, get_historia_clinica, get_paciente} from "@/services/get.js"
import { create_historia_clinica } from "@/services/create.js"
import DatePicker from "@/components/DatePicker.vue"
import EstudiosPdf from "@/components/EstudiosPdf.vue"
import { traducir, convertUTCDateToLocalDate } from "@/utils";
import { calcularEdad, initial_data } from "@/services/utils";
import html2pdf from 'html2pdf.js';
import { getHistoriaClinica, getPeople, getIndicadores, getMedicamentos, getPaciente, createHistoriaClinica, updateHistoriaClinica, updatePaciente } from '@/services/monolito';

export default {
    name: 'HistoriaClinica',
    components: {
        CrudComponent,
        DatePicker,
        EstudiosPdf
    },

    data() {
        return {
            tab: null,
            cabecera: "Ingrese el nombre del estudio/laboratorio",
            cabeceraMedicacion: "Ingrese el nombre del medicamento",
            auth: {
                    username: this.$store.state.credentials.fedacct,
                    password: this.$store.state.credentials.password
                },
            event: null,
            dialog: false,
            dato_medicamento: [],
            datos_medicacion: [],
            estado_consulta: "confirmed",
            datos: [],
            detalleEstudios: [],
            detalleMedicacion: [],
            detalleDatosLaboratorio: [],
            ultimoID : 0,
            datosLaboratorio: [],
            indicadores: [],
            registros: [],
            consultas: [],
            datosTabla: [],
            tratamiento_actual: '',
            consultaEnEdicionId: null,
            medicacion: {
                name: '',
                value: ''
            },
            medicamentosLista: [],
            nuevaMedicacion: { medicacion: null, presentacion: '', dosis: '', frecuencia: '' },
            medicaciones: [],
            nuevaConsulta: {
                fecha_consulta: '',
                fecha_consulta_fin: '',
                profesional: null,
                sintomas: '',
                diagnostico: '',
                tratamiento: '',
                motivo: ''
            },
            estudios: {
                estudio_name: '',
                fecha_estudio: '',
                observaciones: ''
            },
            paciente: {},
            historyLoadError: '',
            pacienteForm: {
                name: '',
                lastname: '',
                dni: '',
                dob: '',
                gender: 'm',
                telefono: '',
                seguroId: '',
                seguroName: '',
                seguroNumber: ''
            },
            seguroOptions: [],
            pacienteEditando: false,
            opcionesGenero: [
                { text: 'MASCULINO', value: 'm' },
                { text: 'FEMENINO', value: 'f' },
                { text: 'OTRO', value: 'o' }
            ],
            seguro: {},
            historiaEstudios: [],
            headers: [
                { text: 'Medicamento', value: 'droga', sortable: false},
                { text: 'Presentación', value: 'presentacion', sortable: false },
                { text: 'Dosis', value: 'dosis', sortable: false },
                { text: 'Frecuencia', value: 'frecuencia', sortable: false },
                { text: 'Eliminar', value: 'actions2', sortable: false},
            ],
            headersLab: [
                { text: 'Nombre del Estudio', value: 'estudio_name', sortable: false},
                { text: 'Fecha', value: 'fecha_estudio', sortable: false },
                { text: 'Observaciones', value: 'observaciones', sortable: false },
                { text: 'Editar', value: 'actions1', sortable: false},
                { text: 'Eliminar', value: 'actions2', sortable: false},
            ],
            itemToUpdate: {},
        };
    },
    mounted() {
      this.event = null
        this.event = this.$route.params.event
        if (this.event) {
          this.estado_consulta= this.event.state
          this.nuevaConsulta.profesional = this.event.healthprof
          this.paciente.id = this.event.patient
        }
        else if (this.$route.name === 'mi-historia-clinica') {
          const userProfile = this.$store.state.user_profile || {};
          this.paciente.id = userProfile.patient_id || userProfile.paciente_id || userProfile.id || null;
        }
        else{
          this.paciente.id = this.$route.params.idPaciente
          this.estado_consulta = ""
        }
        this.cargarObrasSociales();
        if (!this.paciente.id) {
          this.historyLoadError = 'No pudimos identificar su ficha de paciente. Verifique sus datos con el centro médico.';
          return;
        }
        this.searchPaciente()


        // Obtener la fecha y hora actual
        const fechaActual = new Date();

        // Formato fijo dd/mm/yyyy HH:mm:ss (el que espera la API). No usar toLocale*:
        // depende del navegador y puede dar "p. m.", que el backend no interpreta.
        this.nuevaConsulta.fecha_consulta = this.formatDateTimeForApi(fechaActual);

        try{
          this.getHistoriaClinica();
        }
        catch (e) {
        }
       if (!this.esPaciente) try{
          this.getIndicadores();
        }
        catch (e) {
        }

         if (!this.esPaciente) try{
          this.getMedicamentos();
        }
        catch{
        }

    },
    methods: {
        async cargarObrasSociales() {
          try {
            const data = await initial_data();
            this.seguroOptions = Array.isArray(data?.insuranse) ? data.insuranse : [];
          } catch (error) {
            this.seguroOptions = [];
          }
        },
        formatDateTimeForApi(value) {
          if (!value) return '';

          const date = value instanceof Date ? value : new Date(value);
          if (Number.isNaN(date.getTime())) return value;

          const pad = (part) => String(part).padStart(2, '0');
          return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
        },
        formatearFechaEdicion(value) {
          if (!value) return '';

          const date = new Date(value);
          if (Number.isNaN(date.getTime())) return value;

          return date.toLocaleString('es-ES');
        },
        construirPayloadConsulta() {
          this.addItem();

          const payload = {
            ...this.nuevaConsulta,
            estudios: this.historiaEstudios.map((estudio) => ({
              estudio_name: estudio.estudio_name,
              fecha_estudio: estudio.fecha_estudio ? this.formatDateTimeForApi(estudio.fecha_estudio) : '',
              observaciones: estudio.observaciones,
              indicadores: (estudio.indicadores || []).map((indicador) => ({
                nombre: indicador.name || indicador.nombre || '',
                valor: indicador.value || indicador.valor || ''
              }))
            })),
            medicaciones: this.medicaciones.map(med => ({
              medicacion: med.id,
              dosis: med.dosis,
              frecuencia: med.frecuencia
            }))
          };

          if (this.event && this.event.id) {
            payload.event = this.event.id;
          }

          return payload;
        },
        resetFormularioConsulta() {
          const fechaActual = new Date();

          this.consultaEnEdicionId = null;
          this.nuevaConsulta = {
            fecha_consulta: this.formatDateTimeForApi(fechaActual),
            fecha_consulta_fin: '',
            profesional: this.event ? this.event.healthprof : null,
            sintomas: '',
            diagnostico: '',
            tratamiento: '',
            motivo: ''
          };
          this.estudios = {
            estudio_name: '',
            fecha_estudio: '',
            observaciones: ''
          };
          this.medicaciones = [];
          this.historiaEstudios = [];
          this.datosLaboratorio = [];
          this.datosTabla = [];
          this.itemToUpdate = {};
          this.nuevaMedicacion = { medicacion: null, presentacion: '', dosis: '', frecuencia: '' };
        },
        normalizarIndicadores(indicadores = []) {
          return indicadores.map((indicador) => ({
            name: indicador.indicador_name?.nombre_indicador || '',
            value: indicador.valor_referencia || ''
          }));
        },
        editarConsulta(consulta) {
          if (this.esPaciente) return;
          this.consultaEnEdicionId = consulta.id;
          this.nuevaConsulta = {
            fecha_consulta: this.formatDateTimeForApi(consulta.fecha_obj),
            fecha_consulta_fin: consulta.fechaFinObj ? this.formatDateTimeForApi(consulta.fechaFinObj) : this.formatDateTimeForApi(new Date()),
            profesional: consulta.profesionalId,
            sintomas: consulta.sintomas || '',
            diagnostico: consulta.diagnostico || '',
            tratamiento: consulta.tratamiento || '',
            motivo: consulta.motivo || ''
          };
          this.medicaciones = (consulta.medicacion || []).map((med) => ({
            id: med.medicacion?.id,
            droga: med.medicacion?.droga || '',
            presentacion: med.medicacion?.presentacion || '',
            dosis: med.dosis || '',
            frecuencia: med.frecuencia || ''
          }));
          this.historiaEstudios = (consulta.estudios || []).map((estudio, index) => ({
            index,
            id: estudio.id,
            estudio_name: estudio.estudio_name || '',
            fecha_estudio: estudio.fecha_estudio ? this.formatDateTimeForApi(estudio.fecha_estudio) : '',
            observaciones: estudio.observaciones || '',
            indicadores: this.normalizarIndicadores(estudio.estudio_indicador || [])
          }));
          this.estudios = {
            estudio_name: '',
            fecha_estudio: '',
            observaciones: ''
          };
          this.datosLaboratorio = [];
          this.datosTabla = [];
          this.itemToUpdate = {};
          this.tab = 1;
        },
        cancelarEdicionConsulta() {
          this.resetFormularioConsulta();
          this.tab = 0;
        },
        syncPacienteForm() {
          const seguroEmpresa = this.paciente?.seguro?.empresa?.name || this.seguro?.name || '';
          const seguroId = this.paciente?.seguro?.empresa?.id ?? this.paciente?.seguro?.id ?? this.seguro?.id ?? this.pacienteForm?.seguroId ?? '';
          const seguroNumero = this.paciente?.seguro?.number || this.seguro?.number || '';
          const safeName = this.paciente.name || this.paciente.nombre || '';
          const safeLastname = this.paciente.lastname || this.paciente.apellido || '';

          this.pacienteForm = {
            name: safeName,
            lastname: safeLastname,
            dni: this.paciente.ref || this.paciente.dni || this.paciente.id || '',
            dob: this.paciente.dob || '',
            gender: this.paciente.gender || this.paciente.genero || 'm',
            telefono: this.paciente.telefono || this.paciente.phone || '',
            seguroId: seguroId !== null && seguroId !== undefined && seguroId !== '' ? Number(seguroId) : '',
            seguroName: seguroEmpresa,
            seguroNumber: seguroNumero
          };
        },
        activarEdicionPaciente() {
          if (this.esPaciente) return;
          this.syncPacienteForm();
          this.pacienteEditando = true;
        },
        cancelarEdicionPaciente() {
          this.syncPacienteForm();
          this.pacienteEditando = false;
        },
        async guardarPacienteDesdeHistoria() {
          if (this.esPaciente) return;
          const seguroSeleccionado = this.seguroOptions.find((option) => String(option.value) === String(this.pacienteForm.seguroId));
          const seguroIdRaw = this.pacienteForm.seguroId ?? this.seguro?.id ?? this.paciente?.seguro?.empresa?.id ?? this.paciente?.seguro?.id ?? '';
          const seguroId = seguroIdRaw === null || seguroIdRaw === undefined || seguroIdRaw === '' ? null : Number(seguroIdRaw);
          const seguroNombre = seguroSeleccionado?.name || this.pacienteForm.seguroName || this.seguro?.name || '';

          const payload = {
            id: this.paciente.id,
            name: this.pacienteForm.name || this.paciente.name || '',
            lastname: this.pacienteForm.lastname || this.paciente.lastname || '',
            dni: this.pacienteForm.dni || this.paciente.ref || this.paciente.dni || '',
            dob: this.pacienteForm.dob || this.paciente.dob || '',
            gender: this.pacienteForm.gender || this.paciente.gender || 'm',
            telefono: this.pacienteForm.telefono || this.paciente.telefono || '',
            phone: this.pacienteForm.telefono || this.paciente.telefono || '',
            insuranse: Number.isFinite(seguroId) ? seguroId : null,
            insuranseNumber: this.pacienteForm.seguroNumber || this.seguro.number || '',
            email: this.$store?.state?.credentials?.fedacct || this.paciente.mail || this.paciente.email || ''
          };

          if (!payload.name || !payload.lastname || !payload.dni) {
            alert('Debe completar nombre, apellido y DNI antes de guardar.');
            return;
          }

          const resultado = await updatePaciente(payload);

          if (resultado === 200 || resultado?.success) {
            this.paciente = {
              ...this.paciente,
              id: payload.id,
              name: payload.name,
              lastname: payload.lastname,
              ref: payload.dni,
              dni: payload.dni,
              gender: payload.gender,
              dob: payload.dob,
              telefono: payload.telefono,
              phone: payload.phone,
              mail: payload.email,
              email: payload.email,
              seguro: {
                ...this.paciente.seguro,
                empresa: {
                  id: payload.insuranse,
                  name: seguroNombre
                },
                number: payload.insuranseNumber,
                name: seguroNombre,
                id: payload.insuranse
              }
            };

            this.seguro = {
              id: payload.insuranse,
              name: seguroNombre,
              number: payload.insuranseNumber
            };

            this.loadDatosPaciente();
            this.syncPacienteForm();
            this.pacienteEditando = false;

            if (this.$alert) {
              await this.$alert({
                title: 'Actualización exitosa',
                message: 'Los datos del paciente se guardaron correctamente.',
                color: 'success'
              });
            }
          } else {
            const message = resultado?.error || 'No se pudo guardar la información del paciente.';
            if (this.$alert) {
              await this.$alert({
                title: 'Error al guardar',
                message,
                color: 'error'
              });
            } else {
              alert(message);
            }
          }
        },
        async searchPaciente(){
          let response = await getPaciente(this.paciente.id)
          if (response !== 404 && response) {
            this.paciente = response.party || {};
            this.paciente.id = response.id;

            if (response.seguro) {
              this.seguro = {
                ...response.seguro,
                id: response.seguro.empresa?.id ?? response.seguro.id ?? null,
                name: response.seguro.empresa?.name || response.seguro.name || '',
                number: response.seguro.number || ''
              };
            } else {
              this.seguro = {};
            }

            this.loadDatosPaciente();
            this.syncPacienteForm();
          } else if (this.esPaciente) {
            this.historyLoadError = 'No se pudo cargar su ficha de paciente. Intente nuevamente o comuníquese con el centro médico.';
          }
        },
        async generarPDF() {
    // --- INICIO DE PERSONALIZACIÓN ---

    // 1. Pega aquí el código Base64 de tu logotipo que copiaste en el paso anterior.
    // Si no tienes uno, puedes dejar este placeholder o borrar toda la línea.
    const logoBase64 = 'data:image/svg+xml;base64,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';

    // 2. Información de tu centro médico
    const clinicInfo = {
        name: 'Centro Médico Terrada',
        address: 'Terrada 374, Bahia Blanca',
        phone: '0291 4510854',
        email: 'centromedicoterrada@gmail.com'
    };

    // --- FIN DE PERSONALIZACIÓN ---


    const elementToPrint = document.createElement('div');
    let htmlContent = '';

    // Función auxiliar para formatear fechas
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric', month: '2-digit', day: '2-digit'
        });
    };

    // Estilos CSS para el PDF
    const styles = `
    <style>
        body { font-family: Arial, sans-serif; font-size: 10pt; color: #333; }
        .unbreakable { page-break-inside: avoid !important; }
        .pdf-container { padding: 10px; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #007bff; padding-bottom: 8px; margin-bottom: 15px; }
        .header .logo { max-width: 150px; max-height: 75px; }
        .header .clinic-info { text-align: right; font-size: 9pt; }
        .main-title { text-align: center; font-size: 16pt; font-weight: bold; margin-bottom: 20px; color: #333; }
        .patient-info { border: 1px solid #ccc; padding: 12px; border-radius: 8px; margin-bottom: 20px; background-color: #f9f9f9; }
        .patient-info table { width: 100%; border-collapse: collapse; }
        .patient-info td { padding: 4px 0; }
        .patient-info strong { color: #555; }
        .consultation-section { margin-bottom: 20px; page-break-inside: avoid; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden; }
        .consultation-header { background-color: #007bff; color: white; padding: 8px 12px; font-size: 11pt; font-weight: bold; }
        .consultation-content { padding: 12px; }
        .section-title { font-size: 10pt; font-weight: bold; color: #0056b3; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px; margin-top: 12px; margin-bottom: 8px; }
        .section-title:first-child { margin-top: 0; }
        .notes { white-space: pre-wrap; background-color: #fdfdfd; padding: 8px; border-radius: 5px; border: 1px solid #eee; margin: 0; }
        .data-table { width: 100%; margin-top: 8px; border-collapse: collapse; font-size: 9pt; }
        .data-table th, .data-table td { border: 1px solid #ddd; padding: 6px; text-align: left; }
        .data-table th { background-color: #f2f2f2; font-weight: bold; }
        .no-data { color: #888; font-style: italic; padding: 5px 0; }
        p { margin-top: 0; margin-bottom: 5px; }
    </style>
    `;

    // Encabezado del documento
    htmlContent += `
    <div class="pdf-container">
        <div class="header">
            ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" class="logo">` : '<div></div>'}
            <div class="clinic-info">
                <strong>${clinicInfo.name}</strong><br>
                ${clinicInfo.address}<br>
                Tel: ${clinicInfo.phone}<br>
                Email: ${clinicInfo.email}
            </div>
        </div>
        <h1 class="main-title">HISTORIA CLÍNICA</h1>
    `;

    // Sección de Información del Paciente
    htmlContent += `
    <div class="patient-info">
        <table>
            <tr>
                <td width="50%"><strong>Paciente:</strong> ${this.paciente.nombre} ${this.paciente.apellido}</td>
                <td width="50%"><strong>ID Paciente:</strong> ${this.paciente.id}</td>
            </tr>
            <tr>
                <td><strong>Edad:</strong> ${this.paciente.edad} años</td>
                <td><strong>Género:</strong> ${this.paciente.genero}</td>
            </tr>
            <tr>
                <td colspan="2"><strong>Obra Social:</strong> ${this.seguro.name || 'N/A'} (Nº ${this.seguro.number || 'N/A'})</td>
            </tr>
        </table>
    </div>
    `;

    // Secciones de Consultas
    if (this.consultas.length > 0) {
        for (const consulta of this.consultas) {
            htmlContent += `
            <div class="consultation-section">
                <div class="consultation-header unbreakable">
                    Consulta del ${consulta.fecha_obj ? consulta.fecha_obj.toLocaleDateString('es-ES') : consulta.fecha} - Dr(a). ${consulta.medico}
                </div>
                <div class="consultation-content">
                    <div class="section-title unbreakable">Motivo de la Consulta</div>
                    <p class="unbreakable">${consulta.motivo || 'No especificado'}</p>

                    <div class="section-title unbreakable">Notas Clínicas y Tratamiento</div>
                    <p class="notes unbreakable">${consulta.tratamiento || 'Sin notas.'}</p>

                    <div class="section-title unbreakable">Medicación Recetada</div>
            `;

            if (consulta.medicacion && consulta.medicacion.length > 0) {
                htmlContent += '<table class="data-table"><thead><tr class="unbreakable"><th>Medicamento</th><th>Dosis</th><th>Frecuencia</th></tr></thead><tbody>';
                consulta.medicacion.forEach(med => {
                    htmlContent += `
                    <tr class="unbreakable">
                        <td>${med.medicacion.droga}</td>
                        <td>${med.dosis || ''}</td>
                        <td>${med.frecuencia || ''}</td>
                    </tr>
                    `;
                });
                htmlContent += '</tbody></table>';
            } else {
                htmlContent += '<p class="no-data unbreakable">No se recetó medicación.</p>';
            }

            htmlContent += '<div class="section-title unbreakable">Estudios Solicitados</div>';

            if (consulta.estudios && consulta.estudios.length > 0) {
                consulta.estudios.forEach(estudio => {
                    htmlContent += `
                    <table class="data-table">
                        <thead>
                            <tr class="unbreakable">
                                <th colspan="2">${estudio.estudio_name} - ${formatDate(estudio.fecha_estudio)}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="unbreakable">
                                <td width="30%"><strong>Observaciones:</strong></td>
                                <td>${estudio.observaciones || 'Sin observaciones.'}</td>
                            </tr>
                    `;
                    if(estudio.estudio_indicador && estudio.estudio_indicador.length > 0) {
                        htmlContent += '<tr class="unbreakable"><td><strong>Indicadores:</strong></td><td>';
                        estudio.estudio_indicador.forEach(ind => {
                            htmlContent += `${ind.indicador_name.nombre_indicador}: ${ind.valor_referencia}<br>`;
                        });
                        htmlContent += '</td></tr>';
                    }
                    htmlContent += '</tbody></table>';
                });
            } else {
                htmlContent += '<p class="no-data unbreakable">No se solicitaron estudios.</p>';
            }
            htmlContent += '</div></div>';
        }
    } else {
        htmlContent += '<p>No hay consultas registradas para este paciente.</p>';
    }

    htmlContent += '</div>';
    elementToPrint.innerHTML = styles + htmlContent;
    elementToPrint.innerHTML = styles + htmlContent;

    // Opciones de html2pdf.js con pie de página dinámico
    const opt = {
      margin: [0.75, 0.5, 1.0, 0.5], // Margen superior reducido, margen inferior ajustado para el pie de página
      filename: `Historia_Clinica_${this.paciente.apellido}_${this.paciente.nombre}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      // Esta es la configuración más importante. Le decimos que respete las reglas CSS.
      pagebreak: { mode: ['css', 'legacy'] }
    };

    const worker = html2pdf().from(elementToPrint).set(opt);

    // Función para agregar el pie de página
    worker.toPdf().get('pdf').then(function (pdf) {
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150);

        const pageHeight = pdf.internal.pageSize.getHeight();

        const confidentialityText = 'Este documento contiene información confidencial y está protegido por el secreto profesional.';
        pdf.text(confidentialityText, pdf.internal.pageSize.getWidth() / 2, pageHeight - 0.6, { align: 'center' });

        const pageText = `Página ${i} de ${totalPages}`;
        pdf.text(pageText, pdf.internal.pageSize.getWidth() - 0.5, pageHeight - 0.4, { align: 'right' });

        const printDate = `Impreso el: ${new Date().toLocaleDateString('es-ES')}`;
        pdf.text(printDate, 0.5, pageHeight - 0.4);
      }
    }).save();
},
        formatFecha(fecha) {
            if (!fecha) return 'Fecha no especificada';
            const options = { year: 'numeric', month: 'long', day: 'numeric' };
            return new Date(fecha).toLocaleDateString('es-ES', options);
        },
        updateItemIndicadores(item) {
            this.itemToUpdate = item;
            this.estudios = item;
            this.datosTabla = item.indicadores;
        },
        deleteItemIndicadores(item) {
            this.historiaEstudios = this.historiaEstudios.filter((element) => element !== item);
        },
        handlerMedicamento(datoMedicamento) {
            this.medicaciones = datoMedicamento;
        },
        closeDialog() {
            this.dialog = false;
        },
        agregarMedicacion() {
            this.$nextTick(() => {
                // Fix: Removed reference to undefined ref 'medicamentosTable' and property 'items'
                // this.$refs.medicamentosTable.$emit('update:items', this.items);
            });
            this.dialog = true;
        },
        goBack() {
            this.$router.go(-1);
        },
        async getHistoriaClinica() {
            let response = await getHistoriaClinica(this.paciente.id);
            if (response !== 404){
                this.registros = response;
                this.agregarConsulta(response)
            }

        },
        loadDatosPaciente(){
          this.paciente.nombre = this.paciente.name.toUpperCase();
          this.paciente.apellido = this.paciente.lastname.toUpperCase();
          this.paciente.edad = this.paciente.dob ? calcularEdad(this.paciente.dob) : 'ND'
          this.paciente.genero = this.paciente.gender==="m" ? "MASCULINO" : "FEMENINO";
        },
        async crearHistoriaClinica() {
          if (this.esPaciente) return;
          const payload = this.construirPayloadConsulta()

          if (this.estaEditandoConsulta) {
            let response = await updateHistoriaClinica(this.paciente.id, this.consultaEnEdicionId, payload)
            if (response === 403) {
              alert('La consulta ya no puede editarse porque superó las 24 horas desde su creación.');
              await this.getHistoriaClinica();
              this.cancelarEdicionConsulta();
              return;
            }
            if (response !== 400) {
              await this.getHistoriaClinica();
              this.cancelarEdicionConsulta();
            }
            return;
          }

          payload.fecha_consulta_fin = this.formatDateTimeForApi(new Date())
          let response = await createHistoriaClinica(this.paciente.id, payload)
          if (response === 200){
            this.$router.go(-1);
          } else {
            alert(response === 423
              ? 'No se pudo guardar: la consulta no se puede registrar en este momento (423).'
              : 'No se pudo guardar la consulta. Revisá los datos e intentá nuevamente.');
          }
        },
        addItem(){
            if (this.estudios.estudio_name === "" || this.datosLaboratorio === [])
                return;
            try {
                const index = this.historiaEstudios.findIndex(item => item.index === this.itemToUpdate.index);
                if (this.itemToUpdate.index === undefined || index === -1) {
                    //insertar los datos del laboratorio
                    this.estudios.indicadores = this.datosLaboratorio
                    const newId = this.historiaEstudios.length;
                    const newItem = { index: newId, ...this.estudios };
                    this.historiaEstudios.push(newItem);
                }
                else {

                    // Actualizar el item en el array
                    this.estudios.indicadores = this.datosLaboratorio
                    this.historiaEstudios[index] = this.estudios;
                    //this.historia_estudios[index].value = this.datos.value;
                }
                this.estudios = {
                        estudio_name: '',
                        fecha_estudio: '',
                        observaciones: ''
                    }
                this.datosLaboratorio = []
                this.datosTabla = []
                this.itemToUpdate = {}
            }
            catch (error) {
                return
                // Manejar el error según tus necesidades
            }
        },
        addMedicamento(medicine){
            let medicamento = {}
            for (let dato of medicine) {
                    medicamento.name = dato.name
                    medicamento.value = dato.value
                    this.medicaciones.push(medicamento);
            }

        },
        handleSelectMedicine(value) {
            if (value.Medicamento === 'Agregar nuevo valor') {
                this.modalVisible = true; // Mostrar el modal cuando se selecciona "Agregar nuevo valor"
            } else {
                // Manejar la lógica normal de selección de valores
                this.datos_medicacion.Medicamento = value ? value.Medicamento : '';
                this.modalVisible = false;
            }
        },
        detalleConsulta(id) {
            this.registros.forEach(registro => {
                if (registro.id == id) {
                    this.tratamiento_actual = registro.tratamiento
                    this.detalleEstudios = registro.estudios
                    this.detalleMedicacion = registro.medicacion
                }
            });
        },
        async getIndicadores() {
            let response = await getIndicadores();
            if (!Array.isArray(response)) {
                console.warn('getIndicadores: La respuesta no es un array', response);
                return;
            }
            let arrayRenombrado = response.map(function(objeto) {
                return {
                    name: objeto.nombre_indicador,
                    value: objeto.unidad
                };
            });
            this.indicadores = arrayRenombrado;
            const options = [{ name: 'Agregar nuevo valor' }];
            if (this.indicadores && this.indicadores.length > 0) {
                options.push(...this.indicadores);
            }
            this.indicadores = options;
        },
        async getMedicamentos() {
            let response = await getMedicamentos();
            if (!Array.isArray(response)) {
                console.warn('getMedicamentos: La respuesta no es un array', response);
                return;
            }
            this.medicamentosLista = response;
        },
        onMedicamentoSelect(medicamento) {
            if (medicamento) {
                this.nuevaMedicacion.presentacion = medicamento.presentacion || '';
            }
        },
        confirmarMedicacion() {
            if (!this.nuevaMedicacion.medicacion) return;
            this.medicaciones.push({
                id: this.nuevaMedicacion.medicacion.id,
                droga: this.nuevaMedicacion.medicacion.droga,
                presentacion: this.nuevaMedicacion.presentacion,
                dosis: this.nuevaMedicacion.dosis,
                frecuencia: this.nuevaMedicacion.frecuencia
            });
            this.nuevaMedicacion = { medicacion: null, presentacion: '', dosis: '', frecuencia: '' };
        },
          deleteItem(item) {
            this.medicaciones = this.medicaciones.filter((medicacion) => medicacion !== item);
          },
        handleItems(datosLab) {
            // Actualiza los datos del componente padre con los items
            this.datosLaboratorio = datosLab;
        },
        agregarConsulta(datos) {
            this.consultas = []; // Limpiar antes de rellenar
            for (let dato of datos) {
                this.consultas.push({
                    id: dato.id,
                    fecha: convertUTCDateToLocalDate(dato.appointment_date).toLocaleString('es-ES'),
                    fecha_obj: convertUTCDateToLocalDate(dato.appointment_date),
                  fechaFinObj: dato.appointment_date_end ? convertUTCDateToLocalDate(dato.appointment_date_end) : null,
                    medico: dato.profesional.party.name + " " + dato.profesional.party.lastname,
                  profesionalId: dato.profesional ? dato.profesional.id : null,
                    motivo: dato.motivo_consulta,
                  sintomas: dato.sintomas || '',
                  diagnostico: dato.diagnostico || '',
                    tratamiento: dato.tratamiento,
                  canEdit: !!dato.can_edit,
                  editableUntil: dato.editable_until || null,
                    // Añadimos los datos anidados para usarlos directamente en la plantilla
                    estudios: dato.estudios || [],
                    medicacion: dato.medicacion || []
                });
            }
        },
    },
    computed: {
      esPaciente() {
        const profile = this.$store.state.user_profile || {};
        const role = profile.role || (Array.isArray(profile.roles) ? profile.roles[0] : null) || this.$store.state.credentials.role;
        const normalizedRole = typeof role === 'string' ? role.toLowerCase() : '';
        return this.$route.name === 'mi-historia-clinica' || normalizedRole === 'patient' || normalizedRole === 'paciente';
      },
      obra_social_name() {
          return this.paciente.seguro && this.paciente.seguro.empresa ? this.paciente.seguro.empresa.name : "No tiene obra social";
      },
      obra_social_number() {
          return this.paciente.seguro && this.paciente.seguro.number ? this.paciente.seguro.number : "No tiene número de obra social";
      },
      tratamiento(){
          return this.tratamiento_actual
      },
      estado_actual() {
          return this.estado_consulta
      },
        estaEditandoConsulta() {
          return this.consultaEnEdicionId !== null;
        },
        mostrarTabFormulario() {
          return this.puedeAgregarConsulta || this.estaEditandoConsulta;
        },
        formularioConsultaTitulo() {
          return this.estaEditandoConsulta ? 'Editar Consulta' : 'Agregar Consulta';
        },
        guardarConsultaLabel() {
          return this.estaEditandoConsulta ? 'Guardar cambios' : 'Guardar Historia Clínica';
        },
      puedeAgregarConsulta() {
          // Lógica para determinar si se puede agregar una nueva consulta
          return !this.esPaciente && (this.estado_actual === 'confirmed' || this.estado_actual === 'checked_in');
      }
}


};
</script>

<style>
/* Estilos globales para la página para un look más limpio */
.v-card--variant-outlined {
  border-color: rgba(0, 0, 0, 0.12);
}

.v-expansion-panel-text__wrapper {
    padding: 16px 24px 24px;
}

.text-grey-darken-3 {
  color: #424242 !important;
}

.text-grey {
  color: #757575 !important;
}
.v-expansion-panel-content__wrap {
    padding: 0 24px 16px;
}
</style>






